import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { RuneLogo } from "@/components/RuneLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function Login() {
  const { login, loginEmail, registerEmail, setUser } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const [devLoading, setDevLoading] = useState(false);
  // /signup is this same card with the create-account tab preselected — landing CTAs link
  // there so new visitors land on a choice of methods, never straight into Google OAuth.
  const [mode, setMode] = useState(pathname === "/signup" ? "signup" : "signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  // Verification link bounces back here with a status.
  useEffect(() => {
    const v = params.get("verify");
    if (v === "ok") toast.success("Email verified — you're all set.");
    else if (v === "invalid") toast.error("That verification link is invalid or expired.");
  }, [params]);

  // Same-site-only redirect target (e.g. an org invite link) — falls back to /build.
  const redirectTo = params.get("redirect");
  const safeRedirect = redirectTo && redirectTo.startsWith("/") && !redirectTo.startsWith("//") ? redirectTo : "/build";

  const submitEmail = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "signup") {
        const res = await registerEmail(email.trim(), password);
        toast.success(res.verification_email_sent ? "Account created — check your email to verify." : "Account created.");
      } else {
        await loginEmail(email.trim(), password);
      }
      navigate(safeRedirect, { replace: true });
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const forgot = async () => {
    if (!email.trim()) return toast.error("Enter your email first, then click reset.");
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
    } catch (e) { /* always succeeds server-side */ }
    toast.success("If that email has an account, a reset link is on its way.");
  };

  const devLogin = async () => {
    setDevLoading(true);
    try {
      const { data } = await api.post("/auth/dev-login", { email: "wizard@runebox.ai", name: "Dev Wizard" });
      setUser(data.user);
      navigate("/build", { replace: true });
    } catch (e) {
      toast.error("Dev login disabled");
    } finally {
      setDevLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center px-4">
      <div className="pointer-events-none absolute inset-0 opacity-90"
        style={{ backgroundImage: "radial-gradient(700px circle at 50% -10%, rgba(45,212,191,0.12), transparent 60%)" }} />
      <div className="relative w-full max-w-md">
        <div className="rb-grain relative overflow-hidden rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-8 shadow-[0_10px_40px_rgba(0,0,0,0.45)]">
          <div className="flex items-center gap-3">
            <RuneLogo className="h-10 w-10" />
            <div>
              <h1 className="rb-display text-2xl font-semibold tracking-tight text-[color:var(--rb-text)]">Runebox</h1>
              <p className="text-xs text-[color:var(--rb-text-faint)]">Your team's registry for skills, agents & commands</p>
            </div>
          </div>

          <p className="mt-6 text-sm leading-relaxed text-[color:var(--rb-text-dim)]">
            Publish a <span className="rb-mono text-[color:var(--rb-accent)]">SKILL.md</span> once — scanned, reviewed, and synced to every
            engineer's machine. Building solo? Craft skills with the AI builder and share them on the free gallery.
          </p>

          <div className="mt-6 flex rounded-xl border border-[color:var(--rb-border)] bg-[color:var(--rb-code)] p-1" data-testid="login-mode-toggle">
            {[{ v: "signin", l: "Sign in" }, { v: "signup", l: "Create account" }].map((m) => (
              <button key={m.v} onClick={() => setMode(m.v)} data-testid={`login-mode-${m.v}`}
                className={`flex-1 rounded-lg py-1.5 text-xs font-medium transition-colors ${
                  mode === m.v ? "bg-[color:rgba(45,212,191,0.14)] text-[color:var(--rb-accent)]" : "text-[color:var(--rb-text-dim)] hover:text-[color:var(--rb-text)]"
                }`}>
                {m.l}
              </button>
            ))}
          </div>

          <form onSubmit={submitEmail} className="mt-4 space-y-3">
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="login-email-input"
              className="h-11 border-[color:var(--rb-border)] bg-[color:var(--rb-code)] text-[color:var(--rb-text)]" />
            <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder={mode === "signup" ? "Create a password (8+ chars)" : "Password"} data-testid="login-password-input"
              className="h-11 border-[color:var(--rb-border)] bg-[color:var(--rb-code)] text-[color:var(--rb-text)]" />
            <Button type="submit" disabled={busy} data-testid="login-submit-button"
              className="h-11 w-full rounded-xl bg-[color:var(--rb-accent)] text-[color:var(--rb-void)] hover:bg-[color:var(--rb-accent-2)]">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (mode === "signup" ? "Create account" : "Sign in")}
            </Button>
          </form>
          {mode === "signin" && (
            <button onClick={forgot} data-testid="login-forgot-button" className="mt-2 w-full text-center text-xs text-[color:var(--rb-text-faint)] hover:text-[color:var(--rb-text-dim)]">
              Forgot your password?
            </button>
          )}

          <div className="my-5 flex items-center gap-3 text-xs text-[color:var(--rb-text-faint)]">
            <div className="h-px flex-1 bg-[color:var(--rb-border)]" /> or <div className="h-px flex-1 bg-[color:var(--rb-border)]" />
          </div>

          <Button
            onClick={() => login(safeRedirect)}
            data-testid="login-google-button"
            className="h-11 w-full rounded-xl border border-[color:var(--rb-border)] bg-transparent text-[color:var(--rb-text)] hover:bg-white/5"
          >
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24"><path fill="currentColor" d="M21.35 11.1h-9.18v2.92h5.27c-.23 1.4-1.64 4.1-5.27 4.1-3.17 0-5.76-2.62-5.76-5.86s2.59-5.86 5.76-5.86c1.81 0 3.02.77 3.71 1.43l2.53-2.44C16.9 5.4 14.76 4.5 12.17 4.5 7.3 4.5 3.35 8.45 3.35 13.3S7.3 22.1 12.17 22.1c5.2 0 8.64-3.66 8.64-8.81 0-.59-.06-1.04-.16-1.49z"/></svg>
            Continue with Google
          </Button>

          {/* Dev bypass — only in local `yarn start`; CRA strips it from any production build. */}
          {process.env.NODE_ENV === "development" && (
            <button
              onClick={devLogin}
              disabled={devLoading}
              className="mt-3 w-full text-center text-xs text-[color:var(--rb-text-faint)] hover:text-[color:var(--rb-text-dim)]"
              data-testid="dev-login-button"
            >
              {devLoading ? "Entering…" : "Continue as Dev (testing only)"}
            </button>
          )}

          <p className="mt-6 text-center text-xs leading-relaxed text-[color:var(--rb-text-faint)]">
            By continuing you agree to our{" "}
            <Link to="/terms" className="text-[color:var(--rb-accent)] hover:underline">Terms</Link> and{" "}
            <Link to="/privacy" className="text-[color:var(--rb-accent)] hover:underline">Privacy Policy</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
